// "Routines" are marketed as "Background Agents" in the UI; the code name remains "routines".
import {Box, createStyles, Divider, Group, Stack, Text} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import BetaBadge from 'ui-library/badges/BetaBadge';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredSpinner} from 'ui-library/Spinner';

import RoutineCard from './RoutineCard';
import RoutineDetailPanel, {DetailPanelMode} from './RoutineDetailPanel';
import {Routine, RoutineStatus, TriggerType} from './RoutineTypes';
import useRoutines from './useRoutines';

interface RoutinesPageProps {
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
  isExpanded: boolean;
}

const useStyles = createStyles((theme) => ({
  splitContainer: {
    display: 'flex',
    overflow: 'hidden',
    margin: '-16px -24px',
    height: 'calc(100vh - 60px)',
  },
  listStack: {
    flex: '1 1 auto',
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
    maxWidth: 540,
  },
  listStackCentered: {
    maxWidth: 540,
    margin: '0 auto',
  },
  listPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
    padding: '16px 24px',
  },
  detailPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
    borderLeft: `1px solid ${theme.colors.gray[3]}`,
    padding: '16px 24px',
  },
  panelGroup: {
    width: '100%',
    height: '100%',
  },
  resizer: {
    width: '6px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    flexShrink: 0,
    ':hover': {
      backgroundColor: theme.colors.gray[2],
    },
  },
  sectionHeader: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    color: theme.colors.gray[6],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

export default function RoutinesPage({
  timeBasedEnabled,
  eventBasedEnabled,
  isExpanded,
}: RoutinesPageProps): JSX.Element {
  const {routines, isLoading, isError, refetch} = useRoutines();
  const [panelMode, setPanelMode] = useState<DetailPanelMode | null>(null);
  const previousPanelMode = useRef<DetailPanelMode | null>(null);
  const previousIsExpanded = useRef(isExpanded);
  const {classes} = useStyles();

  useEffect(() => {
    const panelJustOpened = previousPanelMode.current == null && panelMode != null;
    if (panelJustOpened && !isExpanded) {
      SidepanelActions.expand();
    }
    previousPanelMode.current = panelMode;
  }, [panelMode, isExpanded]);

  useEffect(() => {
    const wasExpanded = previousIsExpanded.current;
    if (wasExpanded && !isExpanded && panelMode != null) {
      setPanelMode(null);
    }
    previousIsExpanded.current = isExpanded;
  }, [isExpanded, panelMode]);

  const handleClose = useCallback(() => {
    setPanelMode(null);
  }, []);

  const handleMutationSuccess = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleCardClick = useCallback((routine: Routine) => {
    setPanelMode({type: 'edit', routineId: routine.id});
  }, []);

  const handleNewRoutine = useCallback(() => {
    setPanelMode({type: 'create'});
  }, []);

  const enabledTriggerTypes = useMemo(() => {
    const types = new Set<TriggerType>();
    if (timeBasedEnabled) {
      types.add(TriggerType.TIME_BASED);
    }
    if (eventBasedEnabled) {
      types.add(TriggerType.EVENT_BASED);
    }
    return types;
  }, [timeBasedEnabled, eventBasedEnabled]);

  const visibleRoutines = useMemo(
    () => routines.filter((s) => enabledTriggerTypes.has(s.triggerType)),
    [routines, enabledTriggerTypes]
  );

  const activeRoutines = useMemo(
    () =>
      visibleRoutines.filter(
        (s) => s.status === RoutineStatus.ACTIVE || s.status === RoutineStatus.PENDING_REGISTRATION
      ),
    [visibleRoutines]
  );
  const pausedRoutines = useMemo(
    () => visibleRoutines.filter((s) => s.status === RoutineStatus.PAUSED),
    [visibleRoutines]
  );

  const listContent = (
    <Stack spacing="lg" className={classes.listStack}>
      <Stack spacing="sm">
        <Group noWrap={true} position="apart" align="center">
          <Group noWrap={true} spacing="xs" align="center">
            <Text fw="bold" size="xl">
              Background Agents
            </Text>
            <BetaBadge />
          </Group>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            icon="plus"
            size={ButtonSize.SMALL}
            text="Add"
            onClick={handleNewRoutine}
          />
        </Group>
        <Text fw="normal" size="sm" c="dimmed">
          Set up Background Agents to run automatically at a set time or triggered by an event.
        </Text>
      </Stack>

      {isLoading ? (
        <CenteredSpinner />
      ) : isError ? (
        <Text size="sm" c="dimmed">
          Failed to load Background Agents. Please try again.
        </Text>
      ) : (
        <Stack spacing="xl">
          <Stack spacing="sm">
            <Text className={classes.sectionHeader}>Active agents</Text>
            {activeRoutines.map((routine) => (
              <RoutineCard key={routine.id} routine={routine} onClick={handleCardClick} />
            ))}
          </Stack>

          {pausedRoutines.length > 0 && (
            <>
              <Divider />
              <Stack spacing="sm">
                <Text className={classes.sectionHeader}>Paused agents</Text>
                {pausedRoutines.map((routine) => (
                  <RoutineCard key={routine.id} routine={routine} onClick={handleCardClick} />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Stack>
  );

  if (panelMode == null) {
    return <div className={classes.listStackCentered}>{listContent}</div>;
  }

  return (
    <Box className={classes.splitContainer}>
      <PanelGroup direction="horizontal" className={classes.panelGroup}>
        <Panel defaultSize={40} minSize={25} maxSize={70}>
          <Box className={classes.listPanel}>{listContent}</Box>
        </Panel>
        <PanelResizeHandle className={classes.resizer} />
        <Panel defaultSize={60} minSize={30} maxSize={75}>
          <Box className={classes.detailPanel}>
            <RoutineDetailPanel
              key={panelMode.type === 'create' ? 'create' : `edit-${panelMode.routineId}`}
              mode={panelMode}
              onClose={handleClose}
              onMutationSuccess={handleMutationSuccess}
              timeBasedEnabled={timeBasedEnabled}
              eventBasedEnabled={eventBasedEnabled}
            />
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}
